import {
	ResponsiveContainer,
	ComposedChart,
	XAxis,
	YAxis,
	Bar,
	Line,
	LabelList,
	Tooltip,
	BarChart,
	Legend,
	ReferenceLine,
	Scatter,
} from "recharts";
import { useEffect, useState } from "react";
import { numberFormatter } from "../../utils";
import moment from "moment";
import divider from "../../assets/divider-logo.3699f955.svg";

interface DateRange {
  baseline: number | null;
  current: number | null;
}
interface Props {
	chartData: Array<T>;
	heading: any;
	goLiveDate: any;
	page: any;
	dateRange?:DateRange
}
interface T {
	quarter: string;
	actual: number;
	forecastedSpend: number;
	spendNeededForNextTier: number;
	spendShift: number;
	targetGrowthSpend: number;
}
const CustomLabel = ({ payload, page, data }: any) => {
	const { x, y, value, index } = payload;
	const offset = 20;
	// Find the last index where gm is not null
	let lastGmIndex = -1;
	for (let i = data.length - 1; i >= 0; i--) {
		if (data[i].gm !== null) {
			lastGmIndex = i;
			break;
		}
	}
	const showLabel = index === 0 || index === lastGmIndex;
	return (
		<g>
			{showLabel && (
				<>
					<line
						x1={x}
						y1={y}
						x2={x}
						y2={y + offset}
						stroke="gray"
						opacity={0.5}
					/>
					<text
						x={x - offset + 8}
						y={y + offset + 10}
						fill="gray"
						fontSize={`${page == "summary" ? "1.4vh" : "1.3vh"}`}
						opacity={`${page == "summary" ? 0.8 : 1}`}
					>
						{numberFormatter(value, 1) + "%"}
					</text>
				</>
			)}
		</g>
	);
};
const CustomLabel2 = ({ payload, page, data }: any) => {
	const { x, y, value, index } = payload;
	const offset = 20;
	// Find the first and last indices where gmCur is not null
	let firstGmCurIndex = -1;
	let lastGmCurIndex = -1;
	for (let i = 0; i < data.length; i++) {
		if (data[i].gmCur !== null) {
			firstGmCurIndex = i;
			break;
		}
	}
	for (let i = data.length - 1; i >= 0; i--) {
		if (data[i].gmCur !== null) {
			lastGmCurIndex = i;
			break;
		}
	}
	const showLabel =
		(firstGmCurIndex !== -1 && index === firstGmCurIndex) ||
		index === lastGmCurIndex;
	return (
		<g>
			{showLabel && (
				<>
					<line
						x1={x}
						y1={y}
						x2={x}
						y2={y - offset - 10}
						stroke="#071158"
						opacity={0.5}
					/>
					<text
						x={x - offset + 5}
						y={y - offset - 15}
						fill="#012169"
						fontSize={`${page == "summary" ? "1.4vh" : "1.3vh"}`}
						opacity={`${page == "summary" ? 0.8 : 1}`}
					>
						{numberFormatter(value, 1) + "%"}
					</text>
				</>
			)}
		</g>
	);
};
const MarginImprovement = (props: Props) => {
	let data: any = props?.chartData;
	const { page } = props;
	const { goLiveDate } = props;
	const [newData, setNewData] = useState<any>([]);
	const [first4SumGM, setFirst4SumGM] = useState<any>(0);
	const [restSumGM, setRestSumGM] = useState<any>(0);
	if (data?.graph1?.length > 0) {
		data?.graph1?.map((item: any, index: any) => {
			goLiveDate.map((g: any, i: any) => {
				if (item.mwstartDate == g.golive_date) {
					item.master_vendor_name =
						g.master_vendor_name == "BRIDGEPORT FITTINGS, INC."
							? "BP"
							: g.master_vendor_name;
				}
			});
		});
	}
	useEffect(() => {
		let newDataSet: any[] = [];
		let first4SumGM1 = 0;
		let restSumGM1 = 0;
		// First 4 items as-is
		data?.graph1?.slice(0, 4).forEach((item: any) => {
		const gmValue = item.gm * 100;
		first4SumGM1 += gmValue;
		newDataSet.push({
			date: moment.utc(item.mwstartDate).format("MMM"),
			gm: item.gm * 100,
			gmCur: null,
			gmleak: item.gmWoLeakage ? item.gmWoLeakage * 100 : null,
			master_vendor_name:
			item.master_vendor_name === "BRIDGEPORT FITTINGS, INC."
				? "BRIDGEPORT"
				: item.master_vendor_name || null,
			dotValue: item.master_vendor_name
			? item.gm * 100 > 25
				? item.gm * 100 - 5
				: item.gm * 100
			: null,
		});
		});

		const restItems = data?.graph1?.slice(4);

		// Find last two months in restItems
		const months = restItems?.map((item: any) =>
		moment.utc(item.mwstartDate).format("YYYY-MM")
		);
		const uniqueMonths = Array.from(new Set(months));
		const lastTwoMonths = uniqueMonths.slice(-2);

		// Separate items: earlier months and last two months
		const earlierMonthGroups: { [key: string]: any[] } = {};
		const lastTwoMonthWeeks: any[] = [];

		restItems?.forEach((item: any) => {
		const gmValue = item.gm * 100;
		restSumGM1 += gmValue;
		const monthKey = moment.utc(item.mwstartDate).format("YYYY-MM");
		if (lastTwoMonths.includes(monthKey)) {
			// Collect weekly data for last two months
			lastTwoMonthWeeks.push({
			date: moment.utc(item.mwstartDate).format("MMM DD"),
			gm: null,
			gmCur: item.gm * 100,
			gmleak: item.gmWoLeakage ? item.gmWoLeakage * 100 : null,
			master_vendor_name:
				item.master_vendor_name === "BRIDGEPORT FITTINGS, INC."
				? "BRIDGEPORT"
				: item.master_vendor_name || null,
			dotValue: item.master_vendor_name
				? item.gm * 100 > 25
				? item.gm * 100 - 5
				: item.gm * 100
				: null,
			});
		} else {
			// Group by month for earlier months
			if (!earlierMonthGroups[monthKey]) {
			earlierMonthGroups[monthKey] = [];
			}
			earlierMonthGroups[monthKey].push(item);
		}
		});

		// Aggregate grouped months and push merged data
		Object.entries(earlierMonthGroups).forEach(([monthKey, items],index:any) => {
		const totalRev = items.reduce(
			(sum: number, i: any) => sum + i.revenue || 0,
			0
		);
		const totalCost = items.reduce(
			(sum: number, i: any) => sum + i.cost || 0,
			0
		);
		const totalLeakage = items.reduce(
			(sum: number, i: any) => sum + i.leakage || 0,
			0
		);

		newDataSet.push({
			date: moment(monthKey, "YYYY-MM").format(`MMM'YY`),
			gm: index == 0?  ((totalRev - totalCost) / totalRev) * 100 : null,
			gmCur: ((totalRev - totalCost) / totalRev) * 100,
			gmleak: ((totalRev - totalCost + totalLeakage) / totalRev) * 100,
			master_vendor_name: null,
			dotValue: null,
		});
		});

		if(Object.entries(earlierMonthGroups)?.length===0){
			lastTwoMonthWeeks[0].gm=lastTwoMonthWeeks[0].gmCur
		}

		// Finally, append the last two months' weekly data
		newDataSet = [...newDataSet, ...lastTwoMonthWeeks];
		console.log(first4SumGM1,restSumGM1,123);
		setNewData(newDataSet);
		setFirst4SumGM(first4SumGM1)
		setRestSumGM(restSumGM1)
	}, [data]);
	
	// Generate the ticks array for the XAxis
	const ticks = [];
	if (newData && newData.length > 0) {
		// Add all of the first 4 items (monthly data)
		for (let i = 0; i < Math.min(4, newData.length); i++) {
			ticks.push(newData[i].date);
		}

		// For the remaining items (daily data), add every third element starting from index 4
		for (let i = 4; i < newData.length; i += 3) {
			ticks.push(newData[i].date);
		}
	}

	let heading = props?.heading;
	const customTooltip = ({ active, payload }: any) => {
		if (active && payload && payload.length) {
			const data = payload[0].payload;

			const popupStyle = {
				width: "fit-content",
				background: "white",
				border: "1px solid #ccc",
				boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
				borderRadius: "1vh",
				color: "black",
				padding: 0,
			};

			// Determine if it's a week or a month
			const isWeek = data?.date?.split(" ")?.length > 1;
			const headerLabel = isWeek ? "Week:" : "Month:";

			return (
				<div style={popupStyle}>
					{/* Header Row */}
					<div className="w-full flex justify-start h-[3vh] items-center bg-[#f0f4f8] py-[3vh] px-[1vh] textSmall">
						<div className="font-bold uppercase">{headerLabel}</div>
						<div
							className="font-bold ml-1"
							style={{ color: "#3f3f3f" }}
						>
							{data.date}
						</div>
					</div>

					{/* Data Rows */}
					<div className="h-fit pl-[1vh] m-[.5vh]  ">
						<p className="label" style={{ padding: 0, margin: 0 }}>
							<label
								className="graph-label-small"
								style={{
									color: "#5a5a5a",
									fontWeight: "normal",
								}}
							>
								{data.gmCur
									? `Gross Margin ${props?.dateRange?.current||2025} :`
									: `Gross Margin ${props?.dateRange?.baseline||2024} :`}{" "}
							</label>
							<label
								className="graph-label-small"
								style={{
									color: "#3f3f3f",
									fontWeight: "bold",
								}}
							>
								{numberFormatter(data.gmCur ?? data.gm, 2)}%
							</label>
						</p>
					</div>
				</div>
			);
		}
		return null;
	};

	console.log(
		newData,"new Data"
	);

	return (
		<div className={`w-full graphBox h-[100%]`}>
			{/* <div
				className={`bg-[#ffff] graphBorder flex ${
					page == "summary" ? "" : "flex-col"
				} justify-between`}
			>
				<p className="text-center graphTitle pl-[1vh]">
					{" "}
					<span>{`${page == "summary" ? "3. " : "2. "}`}</span>
					{heading}{" "}
				</p>
				<p className="flex justify-end px-[2vh] font-semibold text-[1.8vh] mt-[0] text-[#40a108]">
					{numberFormatter(data?.calculationData?.bps, 1)}bps Margin
					Improvement
				</p>
			</div> */}
			<div className="headSpace">
				<span className="headContent !text-[1vw]"> {heading}</span>
				<p className="flex justify-end px-[2vh] font-semibold textNormal mt-[0] text-[#40a108]">
					{numberFormatter(data?.calculationData?.bps, 1)}bps Margin
					Improvement
				</p>
			</div>
			<ResponsiveContainer
				width="100%"
				height="65%"
				className="bg-[#ffff] px-[3vh] pt-[2vh]"
			>
				<ComposedChart
					data={newData}
					margin={{
						top: 20,
						right: 10,
						left: 20,
					}}
				>
					<XAxis
						dataKey="date"
						className="graph-label-small "
						stroke="black"
						ticks={ticks}
					/>
					<YAxis
						hide
						fontSize="1.5vh"
						domain={[
							(dataMin: any) => dataMin / 1.2,
							(dataMax: any) => dataMax * 1.1,
						]}
						tickFormatter={(value: number) =>
							numberFormatter(value, 0, "$")
						}
					/>
					<Tooltip
						contentStyle={{
							color: "white",
							backgroundColor: "rgb(26, 25, 22)",
						}}
						cursor={{ strokeDasharray: "3 3", fill: "#212121" }}
						content={customTooltip}
					/>

					{first4SumGM > 0 && (
						<ReferenceLine
							y={data?.calculationData?.lastyearGM * 100}
							label={{
								position: "insideTopRight",
								fill: "gray",
								fontStyle: "italic",
								fontSize: "1.8vh",
								value:
									numberFormatter(
										data?.calculationData?.lastyearGM * 100,
										2
									) + "%",
							}}
							stroke="gray"
							strokeDasharray="5 3"
							opacity={1}
						/>
					)}
					{restSumGM > 0 && (
						<ReferenceLine
							y={data?.calculationData?.curryearGM * 100}
							label={{
								position: "insideBottomLeft",
								fill: "#012169",
								fontStyle: "italic",
								fontSize: "1.8vh",
								value:
									numberFormatter(
										data?.calculationData?.curryearGM * 100,
										2
									) + "%",
							}}
							stroke="#012169"
							strokeDasharray="5 3"
							opacity={1}
						/>
					)}
					<Line
						type="linear"
						dataKey="gm"
						stroke="gray"
						name={`2024`}
						strokeDasharray={"0"}
						connectNulls={true}
						strokeWidth={2}
						// dot={page == 'summary' ? true : false}
						dot={
							page == "summary"
								? ($) => (
										<CustomDot
											{...$}
											data={newData}
											color={"gray"}
										/>
								  )
								: false
						}
					>
						<LabelList
							dataKey="gm"
							content={(props) => (
								<CustomLabel
									payload={props}
									page={page}
									data={newData}
								/>
							)}
						/>
					</Line>
					<Line
						type="linear"
						dataKey="gmCur"
						stroke="#012169"
						name={`2025`}
						strokeDasharray={"0"}
						connectNulls={true}
						strokeWidth={2}
						// dot={page == 'summary' ? true : false}
						dot={
							page == "summary"
								? ($) => (
										<CustomDot
											{...$}
											data={newData}
											color={"#012169"}
										/>
								  )
								: false
						}
					>
						<LabelList
							dataKey="gmCur"
							content={(props) => (
								<CustomLabel2
									payload={props}
									page={page}
									data={newData}
								/>
							)}
						/>
					</Line>
					{page == "summary" && (
						<Scatter
							dataKey="dotValue"
							fill="none"
							legendType="none"
						>
							<LabelList
								dataKey="master_vendor_name"
								fill="black"
								fontWeight={700}
								fontSize="1.2vh"
								formatter={(value: number) =>
									`${value} Go-Live`
								}
								position="bottom"
							/>
						</Scatter>
					)}
				</ComposedChart>
			</ResponsiveContainer>
			<div className="contentFooter !justify-end">
				<CustomLegend dateRange={props?.dateRange}></CustomLegend>
			</div>
		</div>
	);
};
const CustomLegend = (props:any) => (
	<div
		style={{
			display: "flex",
			gap: "2rem",
			alignItems: "center",
			color: "black",
			fontSize: "0.8rem",
		}}
	>
		<span>
			<span
				style={{
					display: "inline-block",
					width: 10,
					height: 18,
					background: "gray",
					marginRight: 8,
					verticalAlign: "middle",
				}}
			/>
			{props?.dateRange?.baseline||2024}
		</span>
		<span>
			<span
				style={{
					display: "inline-block",
					width: 10,
					height: 18,
					background: "#012169",
					marginRight: 8,
					verticalAlign: "middle",
				}}
			/>
			{props?.dateRange?.current||2025}
		</span>
	</div>
);
const CustomDot = (props: any) => {
	const { cx, cy, index, data, color } = props;
	// Only render for first and last point
	if (index === 0 || index === data.length - 1) {
		return color == "gray" && index == 0 ? (
			<circle cx={cx} cy={cy} r={5} fill={color} stroke="none" />
		) : color == "#012169" && index !== 0 ? (
			<circle cx={cx} cy={cy} r={5} fill={color} stroke="none" />
		) : (
			<></>
		);
	}
	return null;
};
export default MarginImprovement;
